/**
 * Backend resolution.
 *
 * One decision, made once at startup, based on whether Firebase is
 * configured. Everything downstream calls `getBackend()` and never
 * branches on the mode itself.
 *
 * The Firebase SDK is loaded with a dynamic import so it lands in its own
 * chunk rather than the main bundle. The branded shell can paint while it
 * downloads, and a local-mode development session never fetches it at all
 * (Phase 18).
 */

import {
  resolveBackendMode,
  warnAboutConfigOnce,
} from "../../common/firebase/core/firebaseConfig";
import localBackend from "./localBackend";

let resolved = null;
let pending = null;

export function getBackendMode() {
  return resolveBackendMode();
}

/**
 * Resolve the backend, loading the Firebase chunk if that's the mode.
 * Safe to call repeatedly: the work happens once.
 *
 * @returns {Promise<import("./backendContract").Backend|null>}
 *   null when the build is misconfigured.
 */
export function loadBackend() {
  if (resolved) return Promise.resolve(resolved);
  if (pending) return pending;

  warnAboutConfigOnce();
  const { mode } = resolveBackendMode();

  if (mode === "misconfigured") return Promise.resolve(null);

  if (mode === "local") {
    resolved = localBackend;
    return Promise.resolve(resolved);
  }

  pending = import("./firebaseBackend").then((module) => {
    resolved = module.default;
    pending = null;
    return resolved;
  });

  return pending;
}

/**
 * The already-resolved backend.
 *
 * Local mode resolves synchronously, so services and tests can use this
 * directly. Firebase mode requires `loadBackend()` to have completed,
 * which `AuthProvider` guarantees before any service runs.
 *
 * @returns {import("./backendContract").Backend|null}
 */
export function getBackend() {
  if (resolved) return resolved;

  const { mode } = resolveBackendMode();

  if (mode === "local") {
    resolved = localBackend;
    return resolved;
  }

  if (mode === "misconfigured") return null;

  throw new Error(
    "The Firebase backend has not finished loading. " +
      "Call loadBackend() before using backend services."
  );
}

/** Test-only. */
export function __resetBackendForTests() {
  resolved = null;
  pending = null;
}
